import { Button, Flex, Tooltip, Typography } from "antd";
import styled from "styled-components";
import { CopyIcon, DotsSixVerticalIcon, TrashIcon } from "@phosphor-icons/react";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { CSSProperties, MouseEvent, ReactNode } from "react";
import { useTranslate } from "@refinedev/core";
import { LayerDescriptor } from "../../helpers/layerItemDescriptor";

const StyledRow = styled(Flex)<{ $selected: boolean }>`
  width: 100%;
  padding: 5px 8px;
  cursor: pointer;
  background-color: ${({ $selected, theme }) =>
    $selected ? theme.colorPrimaryBg : "transparent"};

  &:hover {
    background-color: ${({ $selected, theme }) =>
      $selected ? theme.colorPrimaryBg : theme.colorFillTertiary};
  }
`;

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: grab;
  touch-action: none;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const Label = styled(Typography.Text)`
  flex: 1;

  /* Allow the flex child to shrink below its content width so the text can
     actually truncate with an ellipsis. */
  min-width: 0;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type RowProps = {
  descriptor: LayerDescriptor;
  selected: boolean;
  // Receives the click event so callers can detect Cmd/Ctrl+click (multi-select).
  onSelect: (e: MouseEvent) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
};

type PresentationalProps = RowProps & {
  dragHandle?: ReactNode;
  rowRef?: (node: HTMLElement | null) => void;
  style?: CSSProperties;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: Record<string, any>;
  // dnd-kit drag listeners — when present the whole row acts as the drag handle.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dragListeners?: Record<string, any>;
};

const LayerRowView = ({
  descriptor,
  selected,
  onSelect,
  onDelete,
  onDuplicate,
  dragHandle,
  rowRef,
  style,
  attributes,
  dragListeners,
}: PresentationalProps) => {
  const t = useTranslate();
  const Icon = descriptor.icon;
  // Prefer the item's actual text; fall back to the generic per-type label.
  const label = descriptor.name ?? t(descriptor.nameKey);

  return (
    <StyledRow
      ref={rowRef}
      style={style}
      align="center"
      gap={6}
      $selected={selected}
      onClick={onSelect}
      {...attributes}
      {...dragListeners}
    >
      {dragHandle ?? <span style={{ width: 20, flexShrink: 0 }} />}
      <Icon size={18} />
      <Label>{label}</Label>
      {onDuplicate && (
        <Tooltip title={t("components.templates.settings.duplicateItem")}>
          <Button
            type="text"
            size="small"
            aria-label={t("components.templates.settings.duplicateItem")}
            icon={<CopyIcon size={18} />}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          />
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title={t("components.templates.settings.deleteItem")}>
          <Button
            type="text"
            size="small"
            aria-label={t(
              "components.templates.TemplatesBoard.storyboardLeftPanel.removeLayer"
            )}
            icon={<TrashIcon size={18} />}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          />
        </Tooltip>
      )}
    </StyledRow>
  );
};

/** Draggable layer row — must be rendered inside a dnd-kit SortableContext. */
export const SortableLayerRow = (props: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    // Consistent with scene/chapter rows: skip the post-drop layout animation.
  } = useSortable({
    id: props.descriptor.id,
    animateLayoutChanges: () => false,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <LayerRowView
      {...props}
      rowRef={setNodeRef}
      style={style}
      attributes={attributes}
      dragListeners={listeners}
      dragHandle={
        // Visual grip only — the whole row is the drag handle (listeners live on
        // the row), so a drag can start from anywhere except the delete button.
        <DragHandle>
          <DotsSixVerticalIcon size={20} weight="bold" />
        </DragHandle>
      }
    />
  );
};

/** Static layer row (e.g. the body background) — no drag affordance. */
export const StaticLayerRow = (props: RowProps) => <LayerRowView {...props} />;
